import axiosInstance from "@/middleware/axios";
import { newRoomListingEndpoints } from "./endpoints";
import {
  AddNewRoomListingSchemaType,
  CreateRoomResponseType,
  UpdateRoomListingData,
  ListingPublishStatus,
  ListingSchemaType,
  ListingMatches,
  type MatchAlertsResult,
  type AlertListing,
  type ZonePriceStats,
  type ZonePriceDistribution,
  RoomListingImagesUpdateSchemaType,
  type ListingHistoryResponse,
  type ListingStatusHistoryPoint,
  type ListingPriceChangeType,
  type ListingPriceHistoryPoint,
  LISTING_STATUS_CHANGE_TYPE,
} from "./schema";
import { parsePrice } from "@/lib/price";
import type { DocumentItem } from "@/components/molecules/Documents/schema";
import { Query } from "@/types/queryTpes";
import { toApiError } from "@/lib/api-error/apiError";
import { OutputNode } from "@/types/real-estate";

const getListingCoords = async (
  searchParams: URLSearchParams
): Promise<Query<Array<OutputNode>>> => {
  try {
    const response = await axiosInstance.get<Query<Array<OutputNode>>>(
      newRoomListingEndpoints.getListingsCoords + "?" + searchParams
    );
    return response.data;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.log("Error in getRealEstateCoords:", error);

    throw new Error(
      error.response?.data?.message ||
        error.message ||
        "An unknown error occurred while fetching real estate coords data"
    );
  }
};

/**
 * Build the multipart FormData payload for /listing/add (and its admin
 * counterpart). Extracted so the regular + admin actions share the exact
 * same serialization rules.
 */
const buildRoomListingFormData = (
  data: AddNewRoomListingSchemaType,
): FormData => {
  const formData = new FormData();

  // Images: File = new upload (sent as images[idx]); string = existing URL kept
  // from a source (duplicate/prefill) → sent as existingImages for the BE to
  // share by reference instead of re-uploading.
  if (Array.isArray(data.images)) {
    const existingImages: string[] = [];
    let fileIdx = 0;
    data.images.forEach((item) => {
      if (item instanceof File) {
        formData.append(`images[${fileIdx}]`, item);
        fileIdx += 1;
      } else if (typeof item === "string" && item) {
        existingImages.push(item);
      }
    });
    if (existingImages.length > 0) {
      formData.append("existingImages", JSON.stringify(existingImages));
    }
  }

  // Append other fields
  Object.entries(data).forEach(([key, value]) => {
    // Display-only fields kept on the form for the review screen;
    // the BE doesn't need them.
    if (key === "associatedUnitTitle" || key === "associatedPortfolioTitle") {
      return;
    }
    // Translate to the BE's `resourceId` field which links the listing
    // to a portfolio unit after creation.
    if (key === "associatedUnitId") {
      if (typeof value === "string" && value) {
        formData.append("resourceId", value);
      }
      return;
    }

    if (value instanceof Date) {
      formData.append(key, value.toISOString());
      return;
    }

    if (key !== "images") {
      if (typeof value === "object" && value !== null) {
        formData.append(key, JSON.stringify(value));
      } else {
        formData.append(key, value as string);
      }
    }
  });

  return formData;
};

const createRoomListingAction = async ({
  data,
}: {
  data: AddNewRoomListingSchemaType;
}): Promise<
  Query<CreateRoomResponseType> & { matchAlerts?: MatchAlertsResult }
> => {
  try {
    const formData = buildRoomListingFormData(data);
    const response = await axiosInstance.post<
      Query<CreateRoomResponseType> & { matchAlerts?: MatchAlertsResult }
    >(newRoomListingEndpoints.create, formData);
    return response.data;
  } catch (error: unknown) {
    throw new Error("Error in Adding Room Listing:", error);
  }
};

const updateListingMatchAlertsAction = async ({
  listingId,
  enabled,
}: {
  listingId: string;
  enabled: boolean;
}): Promise<Query<{ id: string; matchAlertsEnabled: boolean }>> => {
  const response = await axiosInstance.patch<
    Query<{ id: string; matchAlertsEnabled: boolean }>
  >(newRoomListingEndpoints.matchAlerts(listingId), { enabled });
  return response.data;
};

export type UnsubscribedListing = {
  id: string;
  title: string | null;
  slug: string | null;
};

export type UnsubscribeMatchAlertsResult = {
  // Kept for backward compatibility with the previous single-listing response.
  title: string | null;
  listings: UnsubscribedListing[];
};

const unsubscribeMatchAlertsAction = async ({
  listingId,
  token,
}: {
  listingId: string;
  token: string;
}): Promise<Query<UnsubscribeMatchAlertsResult>> => {
  const response = await axiosInstance.post<Query<UnsubscribeMatchAlertsResult>>(
    newRoomListingEndpoints.matchAlertsUnsubscribe(listingId),
    { token },
    // Never let the unsubscribe page spin forever — surface an error instead.
    { timeout: 20000 },
  );
  return response.data;
};

const getMyAlertListingsAction = async (): Promise<Query<AlertListing[]>> => {
  const response = await axiosInstance.get<Query<AlertListing[]>>(
    newRoomListingEndpoints.myAlerts,
  );
  return response.data;
};

const getRoomListingsForUser = async ({
  userId,
}: {
  userId: string;
}): Promise<Query<ListingSchemaType[]>> => {
  try {
    if (!userId) {
      throw new Error("User ID is required");
    }
    const response = await axiosInstance.get<Query<ListingSchemaType[]>>(
      newRoomListingEndpoints.getForUser(userId)
    );
    return response.data;
  } catch (error: unknown) {
    throw new Error("Error in Fetching Room Listings:", error);
  }
};

const getRoomListingsById = async ({
  listingId,
}: {
  listingId: string;
}): Promise<Query<ListingSchemaType>> => {
  try {
    if (!listingId) {
      throw new Error("Listing ID is required");
    }
    const response = await axiosInstance.get<Query<ListingSchemaType>>(
      newRoomListingEndpoints.byId(listingId)
    );
    return response.data;
  } catch (error: unknown) {
    throw new Error("Error in Fetching Room Listing:", error);
  }
};

const getListingMatchesAction = async ({
  listingId,
}: {
  listingId: string;
}): Promise<Query<ListingMatches>> => {
  if (!listingId) throw new Error("Listing ID is required");
  const response = await axiosInstance.get<Query<ListingMatches>>(
    newRoomListingEndpoints.matchesById(listingId),
  );
  return response.data;
};

const getZonePriceStatsAction = async ({
  listingId,
}: {
  listingId: string;
}): Promise<Query<ZonePriceStats>> => {
  if (!listingId) throw new Error("Listing ID is required");
  const response = await axiosInstance.get<Query<ZonePriceStats>>(
    newRoomListingEndpoints.zonePrice(listingId),
  );
  return response.data;
};

const getZonePriceDistributionAction = async ({
  listingId,
}: {
  listingId: string;
}): Promise<Query<ZonePriceDistribution>> => {
  if (!listingId) throw new Error("Listing ID is required");
  const response = await axiosInstance.get<Query<ZonePriceDistribution>>(
    newRoomListingEndpoints.zonePriceDistribution(listingId),
  );
  return response.data;
};

export type ViewsTimeSeriesGranularity = "day" | "week" | "month";

export type ViewsTimeSeriesPoint = {
  bucket: string;
  views: number;
  uniqueViews: number;
};

export type ViewsTimeSeriesResponse = {
  data: {
    granularity: ViewsTimeSeriesGranularity;
    days: number;
    series: ViewsTimeSeriesPoint[];
  };
};

const getListingViewsTimeseriesAction = async ({
  listingId,
  granularity,
  days,
}: {
  listingId: string;
  granularity: ViewsTimeSeriesGranularity;
  days?: number;
}): Promise<ViewsTimeSeriesResponse> => {
  if (!listingId) throw new Error("Listing ID is required");
  const params = new URLSearchParams({ granularity });
  if (days != null) params.set("days", String(days));
  const response = await axiosInstance.get<ViewsTimeSeriesResponse>(
    `${newRoomListingEndpoints.viewsTimeseries(listingId)}?${params}`,
  );
  return response.data;
};

const getListingHistoryAction = async ({
  listingId,
  changeType,
}: {
  listingId: string;
  changeType?: string;
}): Promise<ListingHistoryResponse> => {
  if (!listingId) throw new Error("Listing ID is required");
  const params = new URLSearchParams();
  if (changeType) params.set("changeType", changeType);
  const query = params.toString();
  const response = await axiosInstance.get<ListingHistoryResponse>(
    `${newRoomListingEndpoints.history(listingId)}${query ? `?${query}` : ""}`,
  );
  return response.data;
};

const getListingStatusHistoryAction = async ({
  listingId,
}: {
  listingId: string;
}): Promise<ListingStatusHistoryPoint[]> => {
  const { data } = await getListingHistoryAction({
    listingId,
    changeType: LISTING_STATUS_CHANGE_TYPE,
  });
  return data.history.map((row) => ({
    previousStatus: row.oldValue,
    status: row.newValue,
    changedAt: row.changedAt,
  }));
};

const getListingPriceHistoryAction = async ({
  listingId,
  changeType,
}: {
  listingId: string;
  changeType: ListingPriceChangeType;
}): Promise<ListingPriceHistoryPoint[]> => {
  const { data } = await getListingHistoryAction({ listingId, changeType });
  return data.history.map((row) => ({
    previousPrice: parsePrice(row.oldValue),
    price: parsePrice(row.newValue),
    changedAt: row.changedAt,
  }));
};

const deleteRoomListingByIdAction = async (
  listingId: string
): Promise<Query<unknown>> => {
  try {
    const response = await axiosInstance.delete<Query<unknown>>(
      newRoomListingEndpoints.byId(listingId)
    );

    return response.data;
  } catch (error: unknown) {
    throw new Error("Error in deleting Listing:", error);
  }
};

const updateRoomListingByIdAction = async ({
  listingId,
  data,
}: {
  listingId: string;
  data: UpdateRoomListingData;
}): Promise<Query<unknown>> => {
  try {
    const response = await axiosInstance.patch<Query<unknown>>(
      newRoomListingEndpoints.byId(listingId),
      data
    );

    return response.data;
  } catch (error: unknown) {
    throw new Error("Error in deleting Listing:", error);
  }
};

const updateListingPublishStatusAction = async ({
  listingId,
  status,
}: {
  listingId: string;
  status: ListingPublishStatus;
}): Promise<Query<unknown>> => {
  try {
    const response = await axiosInstance.post<Query<unknown>>(
      newRoomListingEndpoints.updateListingPublishStatus(listingId),
      { isPublished: status }
    );
    return response.data;
  } catch (error: unknown) {
    throw toApiError(error, "Error in Updating Listing Publish Status");
  }
};

const updateListingResourceIdAction = async ({
  listingId,
  resourceId,
}: {
  listingId: string;
  resourceId: string | null;
}): Promise<Query<unknown>> => {
  try {
    const response = await axiosInstance.patch<Query<unknown>>(
      newRoomListingEndpoints.updateListingResourceId(listingId),
      { resourceId }
    );
    return response.data;
  } catch (error: unknown) {
    throw new Error("Error in Updating Listing Resource ID:", error);
  }
};

export const buildListingImagesFormData = (
  data: RoomListingImagesUpdateSchemaType,
): FormData => {
  const formData = new FormData();

  if (Array.isArray(data.imagesToAdd)) {
    data.imagesToAdd.forEach((file, idx) => {
      if (file instanceof File) {
        formData.append(`imagesToAdd[${idx}]`, file);
      }
    });
  }
  Object.entries(data).forEach(([key, value]) => {
    if (value instanceof Date) {
      formData.append(key, value.toISOString());
      return;
    }
    if (key !== "imagesToAdd") {
      if (typeof value === "object" && value !== null) {
        formData.append(key, JSON.stringify(value));
      } else {
        formData.append(key, value as any);
      }
    }
  });

  return formData;
};

const updateRoomListingImagesAction = async ({
  data,
  listingId,
}: {
  data: RoomListingImagesUpdateSchemaType;
  listingId: string;
}): Promise<Query<unknown>> => {
  const formData = buildListingImagesFormData(data);

  try {
    const response = await axiosInstance.patch<Query<unknown>>(
      newRoomListingEndpoints.updateListingImagesByIf(listingId),
      formData,
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error?.response?.data?.message ||
        error?.message ||
        "Error updating room listing images",
    );
  }
};

const getListingDocumentsAction = async ({
  listingId,
}: {
  listingId: string;
}): Promise<Query<DocumentItem[]>> => {
  const response = await axiosInstance.get<Query<DocumentItem[]>>(
    newRoomListingEndpoints.getListingDocuments(listingId),
  );
  return response.data;
};

const uploadListingDocumentAction = async ({
  listingId,
  files,
}: {
  listingId: string;
  files: File[];
}): Promise<Query<DocumentItem[]>> => {
  const formData = new FormData();
  files.forEach((file) => formData.append("documents", file));

  const response = await axiosInstance.post<Query<DocumentItem[]>>(
    newRoomListingEndpoints.uploadListingDocument(listingId),
    formData,
  );
  return response.data;
};

const deleteListingDocumentAction = async ({
  listingId,
  documentId,
}: {
  listingId: string;
  documentId: string;
}): Promise<Query<unknown>> => {
  const response = await axiosInstance.delete<Query<unknown>>(
    newRoomListingEndpoints.deleteListingDocument(listingId, documentId),
  );
  return response.data;
};

export {
  getListingCoords,
  buildRoomListingFormData,
  createRoomListingAction,
  getRoomListingsForUser,
  getRoomListingsById,
  deleteRoomListingByIdAction,
  updateListingPublishStatusAction,
  updateListingResourceIdAction,
  updateRoomListingByIdAction,
  updateRoomListingImagesAction,
  getListingDocumentsAction,
  uploadListingDocumentAction,
  deleteListingDocumentAction,
  getListingMatchesAction,
  updateListingMatchAlertsAction,
  unsubscribeMatchAlertsAction,
  getMyAlertListingsAction,
  getZonePriceStatsAction,
  getZonePriceDistributionAction,
  getListingViewsTimeseriesAction,
  getListingHistoryAction,
  getListingStatusHistoryAction,
  getListingPriceHistoryAction,
};
